// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CircleX, FileSpreadsheet, KeyRound, SkipForward, TriangleAlert } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, toApiError } from "@/api/api-error";
import { api } from "@/api/client";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { FileDropZone } from "@/components/shared/file-drop-zone";
import { ImportPreviewTable, type PreviewRowMeta } from "@/components/shared/import-preview-table";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { VirtualMultiSelectPopover } from "@/components/shared/virtual-multi-select-popover";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  buildStoreResolver,
  buildUserCreateBody,
  type FieldSources,
  generatePassword,
  initialFieldSources,
  joinList,
  LOCAL_REALM,
  resolveField,
  splitList,
  USER_FIELD_LABEL_KEY,
  USER_IMPORT_FIELDS,
  type UserImportField,
  type UserImportFieldKey,
  validateRows,
} from "@/features/iam/bulk-user-import";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { downloadCsv } from "@/lib/csv";
import {
  type FieldSource,
  type FieldSourceMode,
  isFieldConfigured,
  parseSpreadsheet,
  type SheetData,
} from "@/lib/spreadsheet";
import { cn } from "@/lib/utils";

const MAP_PREVIEW_ROWS = 10;
const PASSWORD_MASK = "••••••••";

/**
 * Outcome of one row. `skipped` means the email or username is already taken (the API answers the
 * create with 409) — expected when re-running a sheet, so it's reported separately from a failure.
 */
type RowStatus = "created" | "skipped" | "failed";

interface RowResult {
  rowIndex: number;
  status: RowStatus;
  error?: string;
  /** Password the user was created with (local realm only) — feeds the credentials download. */
  password?: string;
}

interface BulkCreateUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the groups source with these (e.g. when opened from a group's Users tab). */
  defaultGroups?: string[];
}

type Step = "upload" | "map" | "preview" | "run";

export function BulkCreateUsersDialog({ open, onOpenChange, defaultGroups }: BulkCreateUsersDialogProps) {
  const { t } = useTranslation("back-office");
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.groups.includes("admin") ?? false;
  const { data: context } = useBackofficeContext();

  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetName, setSheetName] = useState("");
  const [sheet, setSheet] = useState<SheetData>({ headers: [], rows: [] });
  const [sources, setSources] = useState<FieldSources>(() => initialFieldSources([], defaultGroups));
  const readSheetRef = useRef<((name: string) => SheetData) | null>(null);

  // Flags stamped on every created user, same as the single dialog's toggles. The reset flag only
  // lands on local-realm users, the FUE flag only on users in an ops group.
  const [requirePasswordReset, setRequirePasswordReset] = useState(false);
  const [ftFueTraining, setFtFueTraining] = useState(false);
  // Row indices selected for creation (preview step). Invalid rows start unchecked.
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [runTotal, setRunTotal] = useState(0);
  const [results, setResults] = useState<RowResult[]>([]);
  const [credentialsSaved, setCredentialsSaved] = useState(false);

  // Default stores resolve against the whole estate: `/v1/context` lists active stores only, but the
  // create endpoint accepts any store by name.
  const { data: allStores, isPending: allStoresPending } = useStoresQuery({
    limit: -1,
    onlyActive: false,
    enabled: open,
  });
  const resolveStore = useMemo(() => buildStoreResolver(allStores?.stores ?? []), [allStores]);

  const columnOptions = useMemo(
    () =>
      sheet.headers.map((header, index) => ({
        label: header || t("bulkImport.unnamedColumn", { index: index + 1 }),
        value: String(index),
      })),
    [sheet.headers, t],
  );
  const sheetOptions = useMemo(() => sheetNames.map((name) => ({ label: name, value: name })), [sheetNames]);
  const realmOptions = useMemo(
    () => (context?.userRealms ?? []).map((r) => ({ label: r, value: r })),
    [context?.userRealms],
  );
  const groupOptions = useMemo(
    () => (context?.userGroups ?? []).map((g) => ({ label: g, value: g })),
    [context?.userGroups],
  );
  const storeOptions = useMemo(
    () => (allStores?.stores ?? []).map((s) => ({ label: `${s.legibleName} (${s.storeName})`, value: s.storeName })),
    [allStores],
  );

  const previewFields = useMemo(
    () => USER_IMPORT_FIELDS.map((field) => ({ key: field.key, label: t(USER_FIELD_LABEL_KEY[field.key]) })),
    [t],
  );

  function reset() {
    setStep("upload");
    setFile(null);
    setFileName("");
    setParseError(null);
    setSheetNames([]);
    setSheetName("");
    setSheet({ headers: [], rows: [] });
    setSources(initialFieldSources([], defaultGroups));
    setRequirePasswordReset(false);
    setFtFueTraining(false);
    readSheetRef.current = null;
    setSelected(new Set());
    setRunning(false);
    setProgress(0);
    setRunTotal(0);
    setResults([]);
    setCredentialsSaved(false);
  }

  function handleOpenChange(next: boolean) {
    if (running) return; // don't let the dialog close mid-run
    if (!next) reset();
    onOpenChange(next);
  }

  async function parseFile() {
    if (!file) return;
    setParseError(null);
    try {
      const workbook = await parseSpreadsheet(file);
      if (workbook.sheetNames.length === 0) {
        setParseError(t("bulkImport.errors.noSheets"));
        return;
      }
      readSheetRef.current = workbook.readSheet;
      const first = workbook.sheetNames[0] ?? "";
      const data = workbook.readSheet(first);
      setFileName(file.name);
      setSheetNames(workbook.sheetNames);
      setSheetName(first);
      setSheet(data);
      setSources(initialFieldSources(data.headers, defaultGroups));
      setStep("map");
    } catch {
      setParseError(t("bulkImport.errors.parseFailed"));
    }
  }

  function handleSheetChange(name: string) {
    const data = readSheetRef.current?.(name) ?? { headers: [], rows: [] };
    setSheetName(name);
    setSheet(data);
    setSources(initialFieldSources(data.headers, defaultGroups));
  }

  function patchSource(key: UserImportFieldKey, patch: Partial<FieldSource>) {
    setSources((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  const validation = useMemo(() => {
    const requiredConfigured = USER_IMPORT_FIELDS.filter((f) => f.required).every((f) =>
      isFieldConfigured(sources[f.key]),
    );
    const rowErrors = validateRows(sheet.rows, sources, {
      realms: context?.userRealms ?? [],
      groups: context?.userGroups ?? [],
      resolveStore,
      isAdmin,
      t: (key, values) => t(key, values),
    });
    const invalidCount = rowErrors.filter((e) => e.length > 0).length;
    return { requiredConfigured, rowErrors, invalidCount, validCount: rowErrors.length - invalidCount };
  }, [sheet, sources, context, resolveStore, isAdmin, t]);

  const previewRows = useMemo<PreviewRowMeta[]>(
    () =>
      sheet.rows.map((_, index) => {
        const errors = validation.rowErrors[index] ?? [];
        return { index, errors, valid: errors.length === 0, exists: false };
      }),
    [sheet.rows, validation.rowErrors],
  );

  const getCell = useCallback(
    (rowIndex: number, field: UserImportFieldKey) => {
      const row = sheet.rows[rowIndex] ?? [];
      if (field === "password") {
        // Passwords never render in clear; non-local realms don't take one at all.
        if (resolveField(row, sources, "realm") !== LOCAL_REALM) return "";
        if (sources.password.mode === "computed") return t("users.bulkCreate.generated");
        return resolveField(row, sources, "password") ? PASSWORD_MASK : "";
      }
      const value = resolveField(row, sources, field);
      if (field === "groups") return joinList(splitList(value));
      if (field === "defaultStore") return resolveStore(value) ?? value;
      return value;
    },
    [sheet.rows, sources, resolveStore, t],
  );

  const selectableIndices = useMemo(() => previewRows.filter((r) => r.valid).map((r) => r.index), [previewRows]);
  const allSelectableSelected = selectableIndices.length > 0 && selectableIndices.every((i) => selected.has(i));

  function goToPreview() {
    setSelected(new Set(selectableIndices));
    setStep("preview");
  }

  function toggleRow(index: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelectableSelected ? new Set() : new Set(selectableIndices));
  }

  async function run() {
    const indices = [...selected].sort((a, b) => a - b);
    setStep("run");
    setRunning(true);
    setProgress(0);
    setRunTotal(indices.length);
    const out: RowResult[] = [];
    for (let i = 0; i < indices.length; i++) {
      const rowIndex = indices[i] ?? 0;
      const row = sheet.rows[rowIndex] ?? [];
      const body = buildUserCreateBody(row, sources, {
        storeName: resolveStore(resolveField(row, sources, "defaultStore")) ?? "",
        generatedPassword: sources.password.mode === "computed" ? generatePassword() : undefined,
        requirePasswordReset,
        ftFueTraining,
      });
      try {
        const res = await api.POST("/v1/users", { body });
        if (res.error) throw toApiError(res.error, res.response, t("users.notifications.createFailed"));
        out.push({ rowIndex, status: "created", password: body.password });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // The detail names the clashing field ("user.email:…" / "user.username:…").
          const isUsername = /user_?name/i.test(err.detail ?? "");
          out.push({
            rowIndex,
            status: "skipped",
            error: isUsername ? t("users.form.validation.usernameExists") : t("users.form.validation.emailExists"),
          });
        } else {
          out.push({ rowIndex, status: "failed", error: err instanceof Error ? err.message : String(err) });
        }
      }
      setProgress(i + 1);
      setResults([...out]);
    }
    setRunning(false);
    queryClient.invalidateQueries({ queryKey: ["users"] });
  }

  const credentialRows = results.filter((r) => r.status === "created" && r.password);

  function downloadCredentials() {
    const headers = ["email", "userName", "fullName", "password"];
    const rows = credentialRows.map((r) => {
      const row = sheet.rows[r.rowIndex] ?? [];
      return [
        resolveField(row, sources, "email"),
        resolveField(row, sources, "userName"),
        resolveField(row, sources, "fullName"),
        r.password ?? "",
      ];
    });
    downloadCsv(`user-credentials-${fileName || "sheet"}.csv`, headers, rows);
    setCredentialsSaved(true);
  }

  function downloadFailures() {
    const failed = results.filter((r) => r.status === "failed");
    // Never echo passwords into the failures file — re-running the sheet supplies them again.
    const fields = USER_IMPORT_FIELDS.filter((f) => f.key !== "password");
    const headers = [...fields.map((f) => f.key), "error"];
    const rows = failed.map((r) => [
      ...fields.map((f) => resolveField(sheet.rows[r.rowIndex] ?? [], sources, f.key)),
      r.error ?? "",
    ]);
    downloadCsv(`user-import-failures-${fileName || "sheet"}.csv`, headers, rows);
  }

  function renderSourceInput(field: UserImportField) {
    const source = sources[field.key];
    if (source.mode === "computed") {
      return <span className="text-xs text-muted-foreground">{t("users.bulkCreate.generatedHint")}</span>;
    }
    if (source.mode === "constant") {
      if (field.constantInput === "realm") {
        return (
          <VirtualSelectPopover
            value={source.value}
            onChange={(v) => patchSource(field.key, { value: v })}
            options={realmOptions}
            placeholder={t("users.form.selectRealm")}
            searchPlaceholder={t("users.form.searchRealms")}
            emptyText={t("users.form.noRealms")}
          />
        );
      }
      if (field.constantInput === "groups") {
        return (
          <VirtualMultiSelectPopover
            values={splitList(source.value)}
            onChange={(values) => patchSource(field.key, { value: joinList(values) })}
            options={groupOptions}
            placeholder={t("users.form.selectGroups")}
            searchPlaceholder={t("users.form.searchGroups")}
            emptyText={t("users.form.noGroups")}
          />
        );
      }
      if (field.constantInput === "store") {
        return (
          <VirtualSelectPopover
            value={source.value}
            onChange={(v) => patchSource(field.key, { value: v })}
            options={storeOptions}
            placeholder={t("users.form.selectStore")}
            searchPlaceholder={t("users.form.searchStores")}
            emptyText={t("users.form.noStores")}
            clearable
          />
        );
      }
      return (
        <Input
          type={field.constantInput === "password" ? "password" : "text"}
          autoComplete={field.constantInput === "password" ? "new-password" : "off"}
          data-1p-ignore
          data-lpignore="true"
          value={source.value}
          onChange={(e) => patchSource(field.key, { value: e.target.value })}
          placeholder={t("bulkImport.valuePlaceholder")}
        />
      );
    }
    const unmapped = field.required && source.column == null;
    return (
      <VirtualSelectPopover
        value={source.column != null ? String(source.column) : ""}
        onChange={(v) => patchSource(field.key, { column: v === "" ? null : Number(v) })}
        options={columnOptions}
        placeholder={t("bulkImport.selectColumn")}
        searchPlaceholder={t("bulkImport.searchColumns")}
        emptyText={t("bulkImport.noColumns")}
        className={cn(unmapped && "border-destructive")}
        clearable={!field.required}
      />
    );
  }

  function renderFieldRow(field: UserImportField) {
    const source = sources[field.key];
    const modes: FieldSourceMode[] = field.computable ? ["column", "constant", "computed"] : ["column", "constant"];
    return (
      <div key={field.key} className="grid grid-cols-[7.5rem_auto_1fr] items-center gap-3">
        <Label className="text-sm">
          {t(USER_FIELD_LABEL_KEY[field.key])}
          {field.required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
        <div className="inline-flex rounded-md border p-0.5">
          {modes.map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => patchSource(field.key, { mode })}
              className={cn(
                "rounded px-2 py-1 text-xs transition-colors",
                source.mode === mode
                  ? "bg-accent font-medium text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(
                field.key === "password" && mode === "computed"
                  ? "users.bulkCreate.mode.generate"
                  : `bulkImport.mode.${mode}`,
              )}
            </button>
          ))}
        </div>
        <div className="min-w-0">{renderSourceInput(field)}</div>
      </div>
    );
  }

  const total = sheet.rows.length;
  const createdCount = results.filter((r) => r.status === "created").length;
  const skippedCount = results.filter((r) => r.status === "skipped").length;
  const failedCount = results.filter((r) => r.status === "failed").length;
  const issues = results.filter((r) => r.status !== "created");
  const pct = runTotal > 0 ? Math.round((progress / runTotal) * 100) : 0;
  // Hold the preview until the store list has landed, so default stores are checked against it.
  const canProceedToPreview = validation.requiredConfigured && validation.validCount > 0 && !allStoresPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "p-0 gap-0 overflow-hidden flex flex-col",
          step === "map" || step === "preview" ? "sm:max-w-[64rem]" : "sm:max-w-2xl",
        )}
        aria-describedby={undefined}
      >
        <div className="flex flex-row max-h-[calc(85vh-4rem)]">
          <WizardPane icon={FileSpreadsheet} />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-6 pt-6">
            <DialogHeader>
              <DialogTitle>{t("users.bulkCreate.title")}</DialogTitle>
            </DialogHeader>

            <div className="-mx-6 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
              {/* ── Step: upload ── */}
              {step === "upload" && (
                <div className="py-2">
                  <FileDropZone
                    accept=".xlsx,.xls,.csv"
                    onSelect={setFile}
                    selected={file ? { name: file.name, size: file.size } : undefined}
                    title={t("bulkImport.uploadPrompt")}
                    subtitle={t("bulkImport.uploadHint")}
                  />
                  {parseError && <p className="mt-3 text-sm text-destructive">{parseError}</p>}
                </div>
              )}

              {/* ── Step: map ── */}
              {step === "map" && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <FileSpreadsheet className="h-4 w-4" />
                      {fileName}
                    </span>
                    <span className="ml-auto text-muted-foreground">{t("bulkImport.rowCount", { count: total })}</span>
                  </div>

                  {sheetNames.length > 1 && (
                    <div className="grid grid-cols-[7.5rem_1fr] items-center gap-3">
                      <Label>{t("bulkImport.sheet")}</Label>
                      <VirtualSelectPopover
                        value={sheetName}
                        onChange={handleSheetChange}
                        options={sheetOptions}
                        placeholder={t("bulkImport.selectSheet")}
                        searchPlaceholder={t("bulkImport.searchSheets")}
                        emptyText={t("bulkImport.noSheetsFound")}
                      />
                    </div>
                  )}

                  <Separator />

                  <div className="flex flex-col gap-2.5">{USER_IMPORT_FIELDS.map(renderFieldRow)}</div>

                  <Separator />

                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-3">
                      <Switch
                        id="bulk-require-password-reset"
                        checked={requirePasswordReset}
                        onCheckedChange={setRequirePasswordReset}
                      />
                      <Label htmlFor="bulk-require-password-reset">{t("users.form.requirePasswordReset")}</Label>
                      <span className="text-xs text-muted-foreground">{t("users.bulkCreate.localOnly")}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch id="bulk-ft-fue-training" checked={ftFueTraining} onCheckedChange={setFtFueTraining} />
                      <Label htmlFor="bulk-ft-fue-training">{t("users.form.ftFueTraining")}</Label>
                      <span className="text-xs text-muted-foreground">{t("users.bulkCreate.opsOnly")}</span>
                    </div>
                  </div>

                  <Separator />

                  <ImportPreviewTable
                    rows={previewRows}
                    fields={previewFields}
                    getCell={getCell}
                    maxRows={MAP_PREVIEW_ROWS}
                  />
                  {total > MAP_PREVIEW_ROWS && (
                    <p className="text-xs text-muted-foreground">
                      {t("bulkImport.previewNote", { shown: MAP_PREVIEW_ROWS, total })}
                    </p>
                  )}

                  <p className="text-sm">
                    {t("bulkImport.validSummary", {
                      valid: validation.validCount,
                      invalid: validation.invalidCount,
                    })}
                  </p>
                  {!validation.requiredConfigured && (
                    <p className="text-sm text-destructive">{t("bulkImport.errors.requiredUnmapped")}</p>
                  )}
                </div>
              )}

              {/* ── Step: preview (select which rows to create) ── */}
              {step === "preview" && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span>{t("bulkImport.selectedSummary", { selected: selected.size, total })}</span>
                    {validation.invalidCount > 0 && (
                      <span className="flex items-center gap-1.5 text-destructive">
                        <CircleX className="h-3.5 w-3.5" />
                        {t("bulkImport.invalidSummary", { count: validation.invalidCount })}
                      </span>
                    )}
                  </div>

                  <ImportPreviewTable
                    rows={previewRows}
                    fields={previewFields}
                    getCell={getCell}
                    selectable
                    selected={selected}
                    onToggle={toggleRow}
                    onToggleAll={toggleAll}
                    allSelected={allSelectableSelected}
                  />
                </div>
              )}

              {/* ── Step: run ── */}
              {step === "run" && (
                <div className="flex flex-col gap-4 py-2">
                  <div className="flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                      <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tabular-nums text-sm text-muted-foreground">
                      {progress}/{runTotal}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {running && <InlineSpinner label={t("users.bulkCreate.creating")} className="py-0" />}
                    <span className="flex items-center gap-1.5 text-primary">
                      <CircleCheck className="h-4 w-4" />
                      {t("bulkImport.createdCount", { count: createdCount })}
                    </span>
                    {skippedCount > 0 && (
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <SkipForward className="h-4 w-4" />
                        {t("bulkImport.skippedCount", { count: skippedCount })}
                      </span>
                    )}
                    <span className={cn("flex items-center gap-1.5", failedCount > 0 && "text-destructive")}>
                      <CircleX className="h-4 w-4" />
                      {t("bulkImport.failedCount", { count: failedCount })}
                    </span>
                  </div>

                  {/* Generated passwords exist nowhere else once the dialog closes. */}
                  {!running && credentialRows.length > 0 && !credentialsSaved && (
                    <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-500">
                      <TriangleAlert className="h-4 w-4 shrink-0" />
                      {t("users.bulkCreate.credentialsWarning")}
                    </p>
                  )}

                  {issues.length > 0 && (
                    <div className="max-h-64 overflow-y-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">{t("bulkImport.rowColumn")}</TableHead>
                            <TableHead>{t("users.form.email")}</TableHead>
                            <TableHead>{t("bulkImport.errorColumn")}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {issues.map((r) => (
                            <TableRow key={r.rowIndex}>
                              <TableCell className="tabular-nums text-muted-foreground">{r.rowIndex + 1}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                {resolveField(sheet.rows[r.rowIndex] ?? [], sources, "email") || "—"}
                              </TableCell>
                              <TableCell
                                className={cn(
                                  r.status === "failed" && "text-destructive",
                                  r.status === "skipped" && "text-muted-foreground",
                                )}
                              >
                                {r.status === "skipped" ? t("users.bulkCreate.skipped", { reason: r.error }) : r.error}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          {step === "upload" && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button onClick={parseFile} disabled={!file}>
                {t("bulkImport.next")}
              </Button>
            </>
          )}
          {step === "map" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                {t("bulkImport.back")}
              </Button>
              <Button onClick={goToPreview} disabled={!canProceedToPreview}>
                {t("bulkImport.next")}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("map")}>
                {t("bulkImport.back")}
              </Button>
              <Button onClick={run} disabled={selected.size === 0}>
                {t("users.bulkCreate.createButton", { count: selected.size })}
              </Button>
            </>
          )}
          {step === "run" && (
            <>
              {!running && failedCount > 0 && (
                <Button variant="outline" onClick={downloadFailures}>
                  {t("bulkImport.downloadFailures")}
                </Button>
              )}
              {!running && credentialRows.length > 0 && (
                <Button variant={credentialsSaved ? "outline" : "default"} onClick={downloadCredentials}>
                  <KeyRound className="h-4 w-4" />
                  {t("users.bulkCreate.downloadCredentials")}
                </Button>
              )}
              <Button
                variant={!running && credentialRows.length > 0 && !credentialsSaved ? "outline" : "default"}
                onClick={() => handleOpenChange(false)}
                disabled={running}
              >
                {running ? t("users.bulkCreate.creating") : t("buttons.close")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
